// src/components/pages/mosque/MosqueDonationOpportunitiesMapPage.tsx
//
// Donation Opportunities (Map + Clustering)
// - Supports ONLY these 2 search paths:
//   1) Region + City
//   2) Location-based search (picked on map or current device location)
//
// Business rules:
// - Cat A decides which API to call
//   * Mosque maintenance -> mosqueDonationService.listOpportunitiesJson
//   * Hygiene            -> hygieneDonationService.listHygieneOpportunitiesGeo
//
// - When switching to LOCATION mode:
//   * reset only mosque/location filters
//   * keep donation category filters
//   * apply current donation filters immediately (because location mode auto-searches)
//
// - Cat A should NOT show "All"
// - Hygiene mode goes only till Cat C

import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  MarkerF,
  MarkerClustererF,
  InfoWindowF,
  useJsApiLoader,
} from "@react-google-maps/api";

import { getMosqueTranslation, useTranslation } from "../../../utils/i18nStub";
import { useLanguage } from "../../../contexts/LanguageContext";
import { GOOGLE_MAPS_API_KEY } from "../../../config";

import Breadcrumbs, { Crumb } from "../../../components/navigation/Breadcrumbs";
import MosqueFilters from "./MosqueFilters";
import "./MosqueFilters.css";
import { MosqueFilterState } from "../../../types/mosque";
import { useNavigate } from "react-router-dom";

// Service layer
const svc =
  require("../../../services/mosqueDonationService") as typeof import("../../../services/mosqueDonationService");

const hygieneSvc =
  require("../../../services/hygieneDonationService") as typeof import("../../../services/hygieneDonationService");

// -----------------------------
// Types
// -----------------------------
type Option = { id: number; name: string };

type DonationFilterState = {
  catAId: number | null;
  catBId: number | null;
  catCId: number | null;
  catDId: number | null;
};

type OpportunityRow = {
  request_id?: number;
  request_name?: string;

  mosque_id?: number;
  number?: string;
  mosque_name?: string;

  latitude?: number | null;
  longitude?: number | null;

  capacity?: number | null;
  women_prayer_room_capacity?: number | null;
  has_women_prayer_room?: string | boolean | null;

  classification_name?: string | null;
  region_name?: string | null;
  city_name?: string | null;
  moia_center_name?: string | null;
  full_address?: string | null;
  district_name?: string | null;

  cat_a_name?: string | null;
  cat_b_name?: string | null;
  cat_c_name?: string | null;
  cat_c_names?: string | null;
  cat_d_name?: string | null;
  cat_e_names?: string | null;

  dist_m?: number | null;

  [k: string]: any;
};

type OpportunityDisplayRow = OpportunityRow & {
  _displayKey: string;
};

type OpportunitiesJson = {
  meta?: {
    pageNo?: number;
    pageSize?: number;
    totalCount?: number;
    pageCount?: number;
  };
  data?: OpportunityRow[];
};

type LatLng = { lat: number; lng: number };

// -----------------------------
// Helpers / constants
// -----------------------------
const emptyMosqueFilters: MosqueFilterState = {
  regionId: null,
  cityId: null,
  centerId: null,
  classificationId: null,
  mosqueTypeId: null,
  hasWomenSection: null,
};

const emptyDonationFilters: DonationFilterState = {
  catAId: null,
  catBId: null,
  catCId: null,
  catDId: null,
};

const DEFAULT_CAT_A_NAME = "أعمال الصيانة التصحيحية والوقائية";
const HYGIENE_CAT_A_ID = 900001;

const DEFAULT_CENTER: LatLng = { lat: 24.7136, lng: 46.6753 };
const DEFAULT_ZOOM = 11;

const RADIUS_KM_OPTIONS = [1, 2, 3, 5, 10, 20] as const;
type RadiusKm = (typeof RADIUS_KM_OPTIONS)[number];

function kmToMeters(km: number) {
  return Math.round(km * 1000);
}

function buildGoogleMapsPinUrl(dest: LatLng) {
  return `https://www.google.com/maps?q=${encodeURIComponent(`${dest.lat},${dest.lng}`)}`;
}

function buildGoogleMapsDirectionsUrl(origin: LatLng, dest: LatLng) {
  const o = `${origin.lat},${origin.lng}`;
  const d = `${dest.lat},${dest.lng}`;
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
    o
  )}&destination=${encodeURIComponent(d)}&travelmode=driving`;
}

const GMAP_LIBS: ("places" | "geometry" | "drawing" | "visualization")[] = [];

const toOption = (row: any): Option => ({
  id: row?.id ?? row?.Id ?? 0,
  name: row?.name ?? row?.Name ?? "",
});

function areDonationFiltersEqual(a: DonationFilterState, b: DonationFilterState) {
  return (
    a.catAId === b.catAId &&
    a.catBId === b.catBId &&
    a.catCId === b.catCId &&
    a.catDId === b.catDId
  );
}

// Spread overlapping markers for better display on map
function spreadNearbyMarkers(rows: OpportunityRow[]): OpportunityDisplayRow[] {
  const groups = new Map<string, OpportunityRow[]>();

  for (const row of rows) {
    if (row.latitude == null || row.longitude == null) continue;

    const key = `${Number(row.latitude).toFixed(4)},${Number(row.longitude).toFixed(4)}`;

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  const result: OpportunityDisplayRow[] = [];

  for (const [groupKey, group] of groups) {
    if (group.length === 1) {
      result.push({
        ...group[0],
        _displayKey: `${groupKey}-0-${group[0].request_id ?? group[0].mosque_id ?? "x"}`,
      });
      continue;
    }

    const baseLat = Number(group[0].latitude);
    const baseLng = Number(group[0].longitude);
    const radius = 0.00035;

    group.forEach((item, index) => {
      const angle = (2 * Math.PI * index) / group.length;
      const latOffset = radius * Math.sin(angle);
      const lngOffset = radius * Math.cos(angle);

      result.push({
        ...item,
        latitude: baseLat + latOffset,
        longitude: baseLng + lngOffset,
        _displayKey: `${groupKey}-${index}-${item.request_id ?? item.mosque_id ?? "x"}`,
      });
    });
  }

  return result;
}

export default function MosqueDonationOpportunitiesMapPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("mosque");
  const { language } = useLanguage();
  const catALabel =
    getMosqueTranslation(language, "donation.filters.catA") ??
    t("donation.filters.catA", "فئة نوع المساهمة");

  const tt = useCallback((key: string, fallback: string) => {
    const v = t(key);
    const last = key.includes(".") ? key.split(".").pop() : key;
    if (!v || v === key || v === last) return fallback;
    return v;
  }, [t]);

  // -----------------------------
  // Google Maps loader
  // -----------------------------
  const { isLoaded, loadError } = useJsApiLoader({
    id: "emasajid-google-maps",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: GMAP_LIBS,
  });

  // -----------------------------
  // Mosque marker icon
  // -----------------------------
  const MOSQUE_ICON_URL = `${process.env.PUBLIC_URL}/dga_v2/assets/img/moiaIcons/mosque-location-256.png`;

  const mosqueIcon = useMemo(() => {
    if (!isLoaded || !(window as any).google) return undefined;

    const g = (window as any).google;
    const W = 44;
    const H = 44;

    return {
      url: MOSQUE_ICON_URL,
      scaledSize: new g.maps.Size(W, H),
      anchor: new g.maps.Point(W / 2, H),
    };
  }, [isLoaded, MOSQUE_ICON_URL]);

  const formatWomen = useCallback(
    (v?: string | boolean | null) => {
      if (v === null || v === undefined) return "—";
      if (typeof v === "boolean") {
        return v ? tt("options.yes", "نعم") : tt("options.no", "لا");
      }

      const s = String(v).trim().toLowerCase();
      if (["1", "true", "t", "yes", "y", "نعم", "✓", "ok"].includes(s)) {
        return tt("options.yes", "نعم");
      }
      if (["0", "false", "f", "no", "n", "لا", "x"].includes(s)) {
        return tt("options.no", "لا");
      }
      return String(v);
    },
    [tt]
  );

  // -----------------------------
  // Filters
  // -----------------------------
  const [mosqueFilters, setMosqueFilters] =
    useState<MosqueFilterState>(emptyMosqueFilters);
  const [donationFilters, setDonationFilters] =
    useState<DonationFilterState>(emptyDonationFilters);

  const [appliedMosqueFilters, setAppliedMosqueFilters] =
    useState<MosqueFilterState>(emptyMosqueFilters);
  const [appliedDonationFilters, setAppliedDonationFilters] =
    useState<DonationFilterState>(emptyDonationFilters);

  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const [pageNo, setPageNo] = useState(1);
  const pageSize = 24;

  // -----------------------------
  // Lookups (Cat A..D)
  // -----------------------------
  const [catA, setCatA] = useState<Option[]>([]);
  const [catB, setCatB] = useState<Option[]>([]);
  const [catC, setCatC] = useState<Option[]>([]);
  const [catD, setCatD] = useState<Option[]>([]);
  const [hygieneCatB, setHygieneCatB] = useState<Option[]>([]);
  const [hygieneCatC, setHygieneCatC] = useState<Option[]>([]);

  const [loadingLookups, setLoadingLookups] = useState(false);

  const isHygieneMode = donationFilters.catAId === HYGIENE_CAT_A_ID;
  const displayCatB = isHygieneMode ? hygieneCatB : catB;
  const displayCatC = isHygieneMode ? hygieneCatC : catC;

  React.useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingLookups(true);
      try {
        const [a, b, c, d, hygieneData] = await Promise.all([
          svc.listCatA(),
          svc.listCatB(),
          svc.listCatC(),
          svc.listCatD(),
          hygieneSvc.getHygieneCategories().catch(() => null),
        ]);

        if (!alive) return;

        const mosqueAOpts = (a ?? []).map(toOption);

        const hygieneAOpt = hygieneData?.catA?.[0];
        const catAWithHygiene = hygieneAOpt
          ? [...mosqueAOpts, { id: HYGIENE_CAT_A_ID, name: hygieneAOpt.name }]
          : mosqueAOpts;

        setCatA(catAWithHygiene);
        setCatB((b ?? []).map(toOption));
        setCatC((c ?? []).map(toOption));
        setCatD((d ?? []).map(toOption));

        setHygieneCatB((hygieneData?.catB ?? []).map(toOption));
        setHygieneCatC((hygieneData?.catC ?? []).map(toOption));

        const defaultOpt = mosqueAOpts.find((o) =>
          o.name.includes("الصيانة التصحيحية") || o.name === DEFAULT_CAT_A_NAME
        );

        if (defaultOpt) {
          setDonationFilters((p) =>
            p.catAId === null ? { ...p, catAId: defaultOpt.id } : p
          );
          setAppliedDonationFilters((p) =>
            p.catAId === null ? { ...p, catAId: defaultOpt.id } : p
          );
        }
      } catch {
        // ignore
      } finally {
        if (alive) setLoadingLookups(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // -----------------------------
  // Location state
  // -----------------------------
  const [radiusKm, setRadiusKm] = useState<RadiusKm>(2);

  const [userLoc, setUserLoc] = useState<LatLng | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  const [pickedLoc, setPickedLoc] = useState<LatLng | null>(null);
  const [isPicking, setIsPicking] = useState(false);

  const effectiveLoc = pickedLoc ?? userLoc ?? null;

  // -----------------------------
  // Data state
  // -----------------------------
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<OpportunityDisplayRow[]>([]);
  const [selected, setSelected] = useState<OpportunityDisplayRow | null>(null);

  const [uiError, setUiError] = useState<string>("");
  const [pageCount, setPageCount] = useState(0);

  // -----------------------------
  // Map state
  // -----------------------------
  const [mapCenter, setMapCenter] = useState<LatLng>(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState<number>(DEFAULT_ZOOM);

  const mapRef = useRef<google.maps.Map | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const panTo = useCallback((pos: LatLng, zoom?: number) => {
    if (!mapRef.current) return;
    mapRef.current.panTo(pos);
    if (typeof zoom === "number") mapRef.current.setZoom(zoom);
  }, []);

  // -----------------------------
  // Location-mode helpers
  // -----------------------------
  const resetMosqueFiltersForLocationMode = useCallback(() => {
    // Reset only mosque filters.
    // Keep donation category filters.
    setMosqueFilters(emptyMosqueFilters);
    setAppliedMosqueFilters(emptyMosqueFilters);
    setFiltersResetKey((k) => k + 1);
    setPageNo(1);

    setItems([]);
    setSelected(null);
    setUiError("");
    setGeoError(null);
  }, []);

  const applyCurrentDonationFiltersForAutoSearch = useCallback(() => {
    // Important:
    // Location mode auto-searches.
    // So current UI category filters must be copied to applied filters first.
    setAppliedDonationFilters((prev) =>
      areDonationFiltersEqual(prev, donationFilters) ? prev : { ...donationFilters }
    );
    setPageNo(1);
  }, [donationFilters]);

  const switchToLocationMode = useCallback(() => {
    resetMosqueFiltersForLocationMode();
    applyCurrentDonationFiltersForAutoSearch();
  }, [resetMosqueFiltersForLocationMode, applyCurrentDonationFiltersForAutoSearch]);

  // -----------------------------
  // Map click = pick location
  // -----------------------------
  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!isPicking) return;

      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;

      const p = { lat, lng };

      switchToLocationMode();

      setPickedLoc(p);
      setUserLoc(null);
      setIsPicking(false);

      setUiError("");
      setGeoError(null);

      setMapCenter(p);
      setMapZoom(13);
      panTo(p, 13);
    },
    [isPicking, panTo, switchToLocationMode]
  );

  // -----------------------------
  // Location actions
  // -----------------------------
  const requestUserLocation = useCallback(() => {
    setGeoError(null);
    setUiError("");
    setGeoLoading(true);

    if (!navigator.geolocation) {
      setGeoLoading(false);
      setGeoError(tt("messages.geoNotSupported", "المتصفح لا يدعم تحديد الموقع."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };

        switchToLocationMode();

        setUserLoc(p);
        setPickedLoc(null);

        setGeoLoading(false);

        setMapCenter(p);
        setMapZoom(13);
        panTo(p, 13);
      },
      (err) => {
        setGeoLoading(false);
        setGeoError(err.message || tt("messages.locationDenied", "تم رفض الوصول إلى الموقع."));
        setUserLoc(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60_000 }
    );
  }, [panTo, switchToLocationMode, tt]);

  const togglePickLocation = useCallback(() => {
    setUiError("");
    setGeoError(null);
    setIsPicking((v) => !v);
  }, []);

  // -----------------------------
  // Build query
  // -----------------------------
  const queryString = useMemo(() => {
    const p = new URLSearchParams();

    if (appliedMosqueFilters.regionId) p.set("regionId", String(appliedMosqueFilters.regionId));
    if (appliedMosqueFilters.cityId) p.set("cityId", String(appliedMosqueFilters.cityId));
    if (appliedMosqueFilters.centerId) p.set("centerId", String(appliedMosqueFilters.centerId));
    if (appliedMosqueFilters.classificationId) {
      p.set("classificationId", String(appliedMosqueFilters.classificationId));
    }

    if (
      appliedMosqueFilters.hasWomenSection !== null &&
      appliedMosqueFilters.hasWomenSection !== undefined
    ) {
      const womenValue = appliedMosqueFilters.hasWomenSection ? "true" : "false";

      if (appliedDonationFilters.catAId === HYGIENE_CAT_A_ID) {
        p.set("hasWomenPrayerRoom", womenValue);
      } else {
        p.set("hasWomenSection", womenValue);
      }
    }

    if (appliedDonationFilters.catAId) {
      if (appliedDonationFilters.catAId === HYGIENE_CAT_A_ID) {
        // Hygiene mode
        p.set("catAId", "1");

        if (appliedDonationFilters.catBId) {
          p.set("catBId", String(appliedDonationFilters.catBId));
        }

        if (appliedDonationFilters.catCId) {
          p.set("catCId", String(appliedDonationFilters.catCId));
        }
      } else {
        // Mosque maintenance mode
        p.set("catAId", String(appliedDonationFilters.catAId));

        if (appliedDonationFilters.catBId) {
          p.set("catBId", String(appliedDonationFilters.catBId));
        }

        if (appliedDonationFilters.catCId) {
          p.set("catCId", String(appliedDonationFilters.catCId));
        }

        if (appliedDonationFilters.catDId) {
          p.set("catDId", String(appliedDonationFilters.catDId));
        }
      }
    }

    if (effectiveLoc) {
      p.set("userLat", String(effectiveLoc.lat));
      p.set("userLng", String(effectiveLoc.lng));
      p.set("radiusM", String(kmToMeters(radiusKm)));
    }

    p.set("pageNo", String(pageNo));
    p.set("pageSize", String(pageSize));

    return p.toString();
  }, [appliedMosqueFilters, appliedDonationFilters, effectiveLoc, radiusKm, pageNo]);

  // -----------------------------
  // Fetch data
  // -----------------------------
  const doSearch = useCallback(async () => {
    setUiError("");
    setSelected(null);

    const hasRegionCity =
      !!appliedMosqueFilters.regionId && !!appliedMosqueFilters.cityId;

    if (!effectiveLoc && !hasRegionCity) {
      setUiError(
        tt(
          "messages.mustSelectRegionCity",
          "يرجى اختيار المنطقة والمدينة أو اختيار موقع من الخريطة ثم الضغط على بحث."
        )
      );
      setItems([]);
      setPageCount(0);
      return;
    }

    setLoading(true);

    try {
      let data: OpportunitiesJson;

      if (appliedDonationFilters.catAId === HYGIENE_CAT_A_ID) {
        data = await hygieneSvc.listHygieneOpportunitiesGeo(queryString);
      } else {
        data = await svc.listOpportunitiesJson(queryString);
      }

      const list = Array.isArray(data?.data) ? data.data : [];
      const cleaned = list.filter((x) => x.latitude != null && x.longitude != null);
      const displayRows = spreadNearbyMarkers(cleaned);

      setItems(displayRows);

      if (appliedDonationFilters.catAId === HYGIENE_CAT_A_ID) {
        setPageCount(0);
      } else {
        setPageCount(data?.meta?.pageCount ?? 0);
      }

      if (effectiveLoc) {
        setMapCenter(effectiveLoc);
        setMapZoom(13);
        panTo(effectiveLoc, 13);
      } else if (displayRows.length > 0) {
        const p = {
          lat: Number(displayRows[0].latitude),
          lng: Number(displayRows[0].longitude),
        };
        setMapCenter(p);
        setMapZoom(12);
        panTo(p, 12);
      }
    } catch (e: any) {
      console.error("map opportunities search failed", e);

      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        tt("messages.failedToLoad", "فشل تحميل الفرص.");

      setItems([]);
      setPageCount(0);
      setUiError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [appliedMosqueFilters, appliedDonationFilters, effectiveLoc, queryString, panTo, tt]);

  // Important:
  // Search should follow queryString changes only.
  // Do NOT put doSearch in deps here, otherwise this page can loop.
  React.useEffect(() => {
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  // -----------------------------
  // Clear all location state
  // -----------------------------
  const clearAllLocation = useCallback(() => {
    setIsPicking(false);
    setPickedLoc(null);
    setUserLoc(null);

    setGeoError(null);
    setUiError("");

    setRadiusKm(2);

    const hasRegionCity =
      !!appliedMosqueFilters.regionId && !!appliedMosqueFilters.cityId;

    if (!hasRegionCity) {
      setItems([]);
      setPageCount(0);
      setSelected(null);
    }
  }, [appliedMosqueFilters.regionId, appliedMosqueFilters.cityId]);

  // -----------------------------
  // Apply / Reset handlers
  // -----------------------------
  const onApplyMosqueFilters = (f: MosqueFilterState) => {
    setMosqueFilters(f);
  };

  const onApplyAllFilters = () => {
    clearAllLocation();

    setAppliedMosqueFilters(mosqueFilters);
    setAppliedDonationFilters(donationFilters);
    setPageNo(1);
  };

  const onResetAllFilters = () => {
    setDonationFilters(emptyDonationFilters);
    setMosqueFilters(emptyMosqueFilters);
    setAppliedMosqueFilters(emptyMosqueFilters);
    setAppliedDonationFilters(emptyDonationFilters);
    setFiltersResetKey((k) => k + 1);
    setPageNo(1);

    setItems([]);
    setSelected(null);
    setUiError("");
    setGeoError(null);
  };

  // -----------------------------
  // Breadcrumbs
  // -----------------------------
  const crumbs: Crumb[] = useMemo(
    () => [
      { label: tt("breadcrumbs.home", "الرئيسية"), to: "/" },
      { label: tt("breadcrumbs.mosqueDonationOpportunitiesMap", "خريطة فرص المساهمة في النظافة والصيانة") },
    ],
    [tt]
  );

  // -----------------------------
  // Open in Google Maps
  // -----------------------------
  const openInGoogleMaps = useCallback(() => {
    const lat = selected?.latitude;
    const lng = selected?.longitude;
    if (lat == null || lng == null) return;

    const dest = { lat: Number(lat), lng: Number(lng) };
    const url = effectiveLoc
      ? buildGoogleMapsDirectionsUrl(effectiveLoc, dest)
      : buildGoogleMapsPinUrl(dest);

    window.open(url, "_blank", "noopener,noreferrer");
  }, [selected, effectiveLoc]);

  // -----------------------------
  // Layout tokens
  // -----------------------------
  const pageWrap = "max-w-7xl mx-auto px-4 md:px-6 py-16";

  const locationCard =
    "relative w-full bg-white rounded-[32px] border border-[#E5E7EB] shadow-[0_12px_40px_rgba(16,24,40,0.08)] px-6 md:px-8 py-6";

  return (
    <div className={pageWrap}>
      <div className="space-y-6">
        <Breadcrumbs items={crumbs} />

        <div className="modern-filters-container">
          <div className="modern-filters-form space-y-6">
            <div>
              <MosqueFilters
                key={`mosque-filters-${filtersResetKey}`}
                embedded
                initial={mosqueFilters}
                onApply={onApplyMosqueFilters}
              />
            </div>

            <div className="border-t border-gray-200/60 pt-4">
              <div className="flex items-end gap-2 flex-wrap">
                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {catALabel}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups}
                      value={donationFilters.catAId ?? ""}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        setDonationFilters((p) => ({
                          ...p,
                          catAId: v,
                          catBId: null,
                          catCId: null,
                          catDId: null,
                        }));
                      }}
                    >
                      <option value="">{tt("donation.filters.selectPlaceholder", "يرجى الإختيار")}</option>
                      {catA.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {tt("donation.filters.catB", "فئة أعمال نوع المساهمة")}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups}
                      value={donationFilters.catBId ?? ""}
                      onChange={(e) =>
                        setDonationFilters((p) => ({
                          ...p,
                          catBId: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    >
                      <option value="">{tt("donation.filters.all", "الكل")}</option>
                      {displayCatB.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {tt("donation.filters.catC", "فئة أعمال الصيانة")}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups}
                      value={donationFilters.catCId ?? ""}
                      onChange={(e) =>
                        setDonationFilters((p) => ({
                          ...p,
                          catCId: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    >
                      <option value="">{tt("donation.filters.all", "الكل")}</option>
                      {displayCatC.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {tt("donation.filters.catD", "فئة تفاصيل أعمال الصيانة")}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups || isHygieneMode}
                      value={donationFilters.catDId ?? ""}
                      onChange={(e) =>
                        setDonationFilters((p) => ({
                          ...p,
                          catDId: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    >
                      <option value="">{tt("donation.filters.all", "الكل")}</option>
                      {catD.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200/60 pt-4">
              <button
                type="button"
                onClick={onResetAllFilters}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={loading}
              >
                {tt("donation.filters.reset", "مسح")}
              </button>

              <button
                type="button"
                onClick={onApplyAllFilters}
                className="px-4 py-2 bg-sa-600 text-white rounded-lg hover:bg-sa-700 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={loading}
              >
                {tt("donation.filters.apply", "تطبيق")}
              </button>
            </div>
          </div>
        </div>

        <div className={locationCard}>
          <div className="text-[15px] font-semibold text-[#111827] text-center w-full">
            {tt("labels.radius", "نطاق البحث")}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <button
                type="button"
                onClick={requestUserLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={geoLoading}
              >
                {geoLoading
                  ? tt("buttons.gettingLocation", "جاري تحديد الموقع...")
                  : tt("buttons.useMyLocation", "استخدم موقعي")}
              </button>

              <button
                type="button"
                onClick={togglePickLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
              >
                {isPicking
                  ? tt("buttons.cancelPick", "إلغاء الاختيار")
                  : tt("buttons.pickLocation", "اختيار موقع على الخريطة")}
              </button>

              <button
                type="button"
                onClick={clearAllLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={!userLoc && !pickedLoc && !isPicking}
                title={tt("buttons.clearLocation", "مسح الموقع")}
              >
                {tt("buttons.clearLocation", "مسح الموقع")}
              </button>
            </div>

            <div className="min-w-[240px] w-full md:w-[280px]">
              <select
                className="modern-filter-select font-arabic"
                value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value) as RadiusKm)}
                disabled={!effectiveLoc}
              >
                {RADIUS_KM_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {k} km
                  </option>
                ))}
              </select>
            </div>
          </div>

          {geoError && (
            <div className="mt-4 rounded-[18px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
              {geoError}
            </div>
          )}

          {uiError && (
            <div className="mt-4 rounded-[18px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
              {uiError}
            </div>
          )}

          {isPicking && (
            <div className="mt-4 rounded-[18px] border border-[#DBEAFE] bg-[#EFF6FF] px-4 py-3 text-sm text-[#1D4ED8]">
              {tt("messages.clickMapToPick", "اضغط على الخريطة لاختيار موقع.")}
            </div>
          )}

          <div className="mt-5 text-[15px] text-[#6B7280] font-semibold text-center">
            {effectiveLoc ? (
              <span>
                {tt("status.locationMode", "وضع الموقع")}:{" "}
                <b className="text-[#111827]">
                  {effectiveLoc.lat.toFixed(5)}, {effectiveLoc.lng.toFixed(5)}
                </b>{" "}
                | {tt("labels.radius", "نطاق البحث")}:{" "}
                <b className="text-[#111827]">{radiusKm} km</b>
              </span>
            ) : (
              <span>
                {tt("status.regionCityMode", "وضع المنطقة/المدينة")}:{" "}
                <b className="text-[#111827]">
                  {appliedMosqueFilters.regionId && appliedMosqueFilters.cityId
                    ? tt("status.ready", "تلقائي")
                    : tt("status.selectRegionCity", "اختر المنطقة + المدينة")}
                </b>
              </span>
            )}
            <span className="mx-2">|</span>
            {tt("status.points", " عدد النتائج")}:{" "}
            <b className="text-[#111827]">{items.length}</b>
          </div>
        </div>

        <div className="dga-card" style={{ borderRadius: "32px", overflow: "hidden" }}>
          <div style={{ height: "70vh", width: "100%" }}>
            {!GOOGLE_MAPS_API_KEY ? (
              <div className="p-4 text-sm">
                {tt("messages.missingApiKey", "مفتاح Google Maps غير موجود في ملف الإعدادات.")}
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm">Failed to load Google Maps: {String(loadError)}</div>
            ) : !isLoaded ? (
              <div className="p-4 text-sm">{tt("messages.loadingMap", "جاري تحميل الخريطة...")}</div>
            ) : (
              <GoogleMap
                onLoad={onMapLoad}
                onClick={onMapClick}
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapCenter}
                zoom={mapZoom}
                options={{
                  clickableIcons: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  streetViewControl: false,
                }}
              >
                {userLoc && (
                  <MarkerF
                    position={userLoc}
                    title={tt("infoWindow.yourLocation", "موقعك")}
                    onClick={() => setSelected(null)}
                  />
                )}

                {pickedLoc && (
                  <MarkerF
                    position={pickedLoc}
                    title={tt("infoWindow.selectedLocation", "الموقع المختار")}
                    onClick={() => setSelected(null)}
                  />
                )}

                <MarkerClustererF
                  options={{
                    maxZoom: 16,
                    gridSize: 45,
                    minimumClusterSize: 2,
                    averageCenter: true,
                    zoomOnClick: false,
                  }}
                  onClick={(cluster) => {
                    const map = mapRef.current;
                    if (!map) return;

                    const center = cluster.getCenter();
                    if (center) map.panTo(center);

                    const bounds = cluster.getBounds();
                    if (bounds) map.fitBounds(bounds, 40);

                    window.setTimeout(() => {
                      const currentZoom = map.getZoom() ?? 12;
                      if (currentZoom < 18) {
                        map.setZoom(Math.min(currentZoom + 2, 18));
                      }
                    }, 220);
                  }}
                >
                  {(clusterer) => (
                    <>
                      {items.map((m) => {
                        if (m.latitude == null || m.longitude == null) return null;

                        const title = m.mosque_name ?? tt("unknown_mosque", "مسجد غير معروف");

                        return (
                          <MarkerF
                            key={m._displayKey}
                            position={{ lat: Number(m.latitude), lng: Number(m.longitude) }}
                            clusterer={clusterer}
                            onClick={() => setSelected(m)}
                            title={title}
                            icon={mosqueIcon}
                          />
                        );
                      })}
                    </>
                  )}
                </MarkerClustererF>

                {selected && selected.latitude != null && selected.longitude != null && (
                  <InfoWindowF
                    position={{ lat: Number(selected.latitude), lng: Number(selected.longitude) }}
                    onCloseClick={() => setSelected(null)}
                  >
                    <div style={{ maxWidth: 320 }}>
                      <div className="font-semibold">
                        {selected.mosque_name ?? tt("unknown_mosque", "مسجد غير معروف")}
                      </div>

                      <div className="text-sm opacity-80">
                        {selected.classification_name ?? "—"}
                      </div>

                      <div className="mt-2 text-sm">
                        <div>
                          <b>{tt("infoWindow.women", "مصلى النساء")}:</b>{" "}
                          {formatWomen(selected.has_women_prayer_room as any)}
                        </div>

                        {selected.dist_m != null && (
                          <div>
                            <b>{tt("infoWindow.distance", "المسافة")}:</b>{" "}
                            {(Number(selected.dist_m) / 1000).toFixed(2)} km
                          </div>
                        )}

                        <div className="mt-2 text-xs opacity-80">
                          {selected.full_address ?? "—"}
                        </div>

                        <div className="mt-2 text-xs text-gray-700 font-arabic">
                          <div className="flex flex-wrap gap-2">
                            <span className="px-2 py-1 rounded-full border border-gray-200 bg-gray-50">
                              A: {selected.cat_a_name ?? "-"}
                            </span>
                            <span className="px-2 py-1 rounded-full border border-gray-200 bg-gray-50">
                              B: {selected.cat_b_name ?? "-"}
                            </span>
                            <span className="px-2 py-1 rounded-full border border-gray-200 bg-gray-50">
                              C: {selected.cat_c_name ?? selected.cat_c_names ?? "-"}
                            </span>

                            {appliedDonationFilters.catAId !== HYGIENE_CAT_A_ID && (
                              <>
                                <span className="px-2 py-1 rounded-full border border-gray-200 bg-gray-50">
                                  D: {selected.cat_d_name ?? "-"}
                                </span>
                                <span className="px-2 py-1 rounded-full border border-gray-200 bg-gray-50">
                                  E: {selected.cat_e_names || "-"}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          // Decide which detail flow should open.
                          // - hygiene mode  => HDR / hygiene detail endpoint
                          // - normal mode   => DDR / mosque maintenance detail endpoint
                          const kind: "ddr" | "hdr" =
                            appliedDonationFilters.catAId === HYGIENE_CAT_A_ID ? "hdr" : "ddr";

                          navigate(`/mosque/donation-details/${selected.request_id}?kind=${kind}`, {
                            state: {
                              fromList: true,
                              backTo: "/mosque/donation-opportunities-map",
                              item: selected,

                              // Source-of-truth flag for the detail page
                              kind,

                              // Keep backward compatibility with any old logic using source
                              source: kind === "hdr" ? "hygiene" : undefined,
                            },
                          });
                        }}
                        className="w-full rounded-xl bg-sa-600 px-6 py-3 text-base font-semibold text-white hover:bg-sa-700 transition-all duration-300"
                      >
                        عرض التفاصيل
                      </button>

                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className="dga-btn dga-btn-secondary"
                          style={{
                            width: 34,
                            height: 34,
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={openInGoogleMaps}
                          title={tt("buttons.openInGoogleMaps", "فتح في خرائط Google")}
                          aria-label={tt("buttons.openInGoogleMaps", "فتح في خرائط Google")}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M12 22s7-5.6 7-13a7 7 0 1 0-14 0c0 7.4 7 13 7 13z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinejoin="round"
                            />
                            <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
                          </svg>
                        </button>
                      </div>

                      {!effectiveLoc && (
                        <div className="mt-2 text-xs opacity-70">
                          {tt(
                            "infoWindow.tipForDirections",
                            'معلومة: استخدم "استخدم موقعي" أو "اختيار موقع" لفتح الاتجاهات تلقائيًا.'
                          )}
                        </div>
                      )}
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            )}
          </div>
        </div>

        {pageCount > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <button
              className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              disabled={pageNo <= 1}
              onClick={() => setPageNo((p) => Math.max(1, p - 1))}
            >
              {tt("list.prev", "السابق")}
            </button>

            <div className="text-neutral-500 text-sm font-arabic">
              {`صفحة ${pageNo} من ${pageCount}`}
            </div>

            <button
              className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              disabled={pageNo >= pageCount}
              onClick={() => setPageNo((p) => p + 1)}
            >
              {tt("list.next", "التالي")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}